"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { resolveApiUrl } from "@/data/runtime-data";

type Dish = {
  name: string;
  price: string;
  description: string;
};

type VisitDetails = {
  addressLine1: string;
  addressLine2: string;
  email: string;
  phone: string;
  phoneHref: string;
  mapLink: string;
  hours: { label: string; value: string }[];
  kitchenCloseDisplay?: string;
};

type Notice = { id?: string; message: string; active?: boolean; startDate?: string; endDate?: string };

type DebugFetchStatus = {
  ok: boolean;
  status?: number;
  durationMs?: number;
  error?: string;
};

type DebugInfo = {
  server: {
    menu: DebugFetchStatus;
    hours: DebugFetchStatus;
    contact: DebugFetchStatus;
  };
};

interface HomeLandingProps {
  heroImages: string[];
  featuredDishes: Dish[];
  menuCategories: string[];
  galleryImages: string[];
  visitDetails: VisitDetails;
  debugInfo?: DebugInfo;
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
  },
};

const formatKitchen = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const normalizedHour = hours % 12 || 12;
  return `${normalizedHour}:${minutes.toString().padStart(2, "0")} ${period}`;
};

export default function HomeLanding({
  heroImages,
  featuredDishes,
  menuCategories,
  galleryImages,
  visitDetails,
  debugInfo,
}: HomeLandingProps) {
  const [liveVisitDetails, setLiveVisitDetails] = useState<VisitDetails>(visitDetails);
  const [heroNotices, setHeroNotices] = useState<Notice[]>([]);
  const [selectedPress, setSelectedPress] = useState<{ src: string; alt: string; label: string } | null>(null);
  const [clientDebug, setClientDebug] = useState<Record<string, DebugFetchStatus>>({});
  const pressLogos = [
    { src: "/unchai-images/globemail.png", alt: "The Globe and Mail feature", label: "The Globe and Mail" },
    { src: "/unchai-images/vansun.png", alt: "The Vancouver Sun feature", label: "The Vancouver Sun" },
    { src: "/unchai-images/alexgill.png", alt: "Alex Gill Review", label: "Alex Gill Review" },
    { src: "/unchai-images/RAFAL_GERSZAK_3ATHE_GLOBE_AND_MAIL.jpeg", alt: "Photography feature", label: "Photography feature" },
  ];

  useEffect(() => {
    const load = async () => {
      try {
        const [contactRes, hoursRes] = await Promise.all([
          fetch(resolveApiUrl("/api/contact"), { cache: "no-store" }),
          fetch(resolveApiUrl("/api/hours"), { cache: "no-store" }),
        ]);

        const contact = (contactRes.ok ? await contactRes.json() : {}) as {
          email?: string;
          phoneDisplay?: string;
          phoneHref?: string;
          addressLine1?: string;
          addressLine2?: string;
        };
        const hours = (hoursRes.ok ? await hoursRes.json() : {}) as {
          weeklyHours?: { label: string; value: string }[];
          kitchenCloseTime?: string;
        };

        setLiveVisitDetails((prev) => ({
          ...prev,
          email: contact.email ?? prev.email,
          phone: contact.phoneDisplay ?? prev.phone,
          phoneHref: contact.phoneHref ?? prev.phoneHref,
          addressLine1: contact.addressLine1 ?? prev.addressLine1,
          addressLine2: contact.addressLine2 ?? prev.addressLine2,
          hours:
            hours.weeklyHours?.map((h: any) => ({ label: h.label, value: h.value })) ??
            prev.hours,
          kitchenCloseDisplay:
            hours.kitchenCloseTime
              ? formatKitchen(hours.kitchenCloseTime)
              : prev.kitchenCloseDisplay,
        }));
      } catch (error) {
        console.warn("Failed to refresh visit details from API", error);
      }
    };
    void load();
  }, []);

  useEffect(() => {
    const loadNotices = async () => {
      try {
        const res = await fetch(resolveApiUrl("/api/notices"), { cache: "no-store" });
        if (!res.ok) return;
        const json = (await res.json()) as { notices?: Notice[] };
        const active = (json.notices || []).filter((n) => n.active !== false);
        if (active.length) setHeroNotices(active);
      } catch (error) {
        console.warn("Failed to load notices for hero", error);
      }
    };
    void loadNotices();
  }, []);

  useEffect(() => {
    if (!debugInfo) return;
    const endpoints = [
      { key: 'menu', path: '/api/menu' },
      { key: 'hours', path: '/api/hours' },
      { key: 'contact', path: '/api/contact' },
      { key: 'notices', path: '/api/notices' },
    ];

    const loadDebug = async () => {
      const entries = await Promise.all(
        endpoints.map(async ({ key, path }) => {
          const start = performance.now();
          try {
            const res = await fetch(resolveApiUrl(path), { cache: 'no-store' });
            const durationMs = Math.round(performance.now() - start);
            return [key, { ok: res.ok, status: res.status, durationMs }] as const;
          } catch (error) {
            const durationMs = Math.round(performance.now() - start);
            return [
              key,
              {
                ok: false,
                durationMs,
                error: error instanceof Error ? error.message : String(error),
              },
            ] as const;
          }
        })
      );
      setClientDebug(Object.fromEntries(entries));
    };

    void loadDebug();
  }, [debugInfo]);

  const renderDebugRow = (label: string, entry?: DebugFetchStatus) => {
    if (!entry) {
      return (
        <div key={label} className="flex items-center justify-between gap-3 text-xs text-gray-500">
          <span>{label}</span>
          <span>pending</span>
        </div>
      );
    }
    const status = entry.ok ? 'ok' : 'fail';
    const duration = entry.durationMs !== undefined ? `${entry.durationMs}ms` : '';
    const errorMessage = entry.error ? ` · ${entry.error}` : '';
    return (
      <div
        key={label}
        className={`flex items-center justify-between gap-3 text-xs ${
          entry.ok ? 'text-emerald-700' : 'text-rose-700'
        }`}
      >
        <span>{label}</span>
        <span>
          {status}
          {entry.status ? ` (${entry.status})` : ''}
          {duration ? ` · ${duration}` : ''}
          {errorMessage}
        </span>
      </div>
    );
  };

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden text-gray-900 bg-amber-50">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,rgba(251,191,36,0.3),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(245,158,11,0.25),transparent_30%)]" />
        <div className="container mx-auto px-6 py-16 md:py-24 relative grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <p className="eyebrow text-amber-600">Burrard Street · Vancouver</p>
            <motion.h1
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-balance text-gray-900"
            >
              Unchai Thai Restaurant
            </motion.h1>
            {heroNotices.length > 0 && (
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ delay: 0.05 }}
                className="mt-4"
              >
                <div className="inline-flex flex-wrap items-center gap-2 rounded-2xl bg-rose-600 text-white px-4 py-2 shadow-md shadow-rose-200/50">
                  <span className="text-xs uppercase tracking-wide font-semibold bg-white/20 px-2 py-1 rounded-full">
                    Notice
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {heroNotices.map((notice) => (
                      <span key={notice.id || notice.message} className="text-sm font-semibold leading-snug">
                        {notice.message}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ delay: 0.1, duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
              className="mt-6 text-lg md:text-xl text-gray-800 max-w-2xl leading-relaxed"
            >
              Fast-casual Thai noodle soups, curries, stir-fries, and desserts made with homemade pastes and bright herbs.
              Dine in or call ahead for takeout.
            </motion.p>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ delay: 0.2, duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <Link href="/menu" className="btn btn-primary">
                View full menu
              </Link>
              <Link href={liveVisitDetails.phoneHref} className="btn btn-secondary">
                Call {liveVisitDetails.phone}
              </Link>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ delay: 0.3, duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
              className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              <div className="border border-amber-100 rounded-2xl p-4 bg-white">
                <p className="text-sm font-semibold text-amber-700">Hours</p>
                <ul className="mt-2 space-y-1 text-sm text-gray-800 break-words">
                  {liveVisitDetails.hours.map((entry) => (
                    <li key={entry.label} className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2">
                      <span className="flex-1">{entry.label}</span>
                      <span className="font-semibold text-right whitespace-normal">{entry.value}</span>
                    </li>
                  ))}
                </ul>
                {liveVisitDetails.kitchenCloseDisplay && (
                  <p className="text-xs text-gray-600 mt-2">
                    Kitchen closes at {liveVisitDetails.kitchenCloseDisplay}.
                  </p>
                )}
              </div>
              <div className="border border-amber-100 rounded-2xl p-4 bg-white">
                <p className="text-sm font-semibold text-amber-700">Find us</p>
                <p className="text-sm text-gray-800 leading-relaxed mt-1">
                  {liveVisitDetails.addressLine1}
                  <br />
                  {liveVisitDetails.addressLine2}
                </p>
                <Link
                  href="https://maps.google.com/?q=Unchai+Thai+Restaurant+2351+Burrard+Street+Vancouver"
                  className="mt-2 inline-flex text-xs font-semibold text-amber-700 hover:text-black"
                >
                  View map
                </Link>
              </div>
              <div className="border border-amber-100 rounded-2xl p-4 bg-white">
                <p className="text-sm font-semibold text-amber-700">Contact</p>
                <p className="text-sm text-gray-800 mt-1 break-words">Phone: {liveVisitDetails.phone}</p>
                <p className="text-sm text-gray-800 break-words">Email: {liveVisitDetails.email}</p>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
            className="grid grid-cols-2 gap-4"
          >
            {heroImages.map((src, idx) => (
              <div
                key={src}
                className={`rounded-3xl overflow-hidden shadow-lg border border-amber-100 ${
                  idx === 0 ? "col-span-2 h-72 md:h-96" : "h-40 md:h-48"
                }`}
              >
                <img
                  src={src}
                  alt="Unchai Thai dish"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container max-w-6xl grid gap-10 lg:grid-cols-2 items-start">
          <div className="space-y-3">
            <p className="eyebrow">Press &amp; Awards</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Trusted by Vancouver food media</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Local outlets have highlighted Unchai for comforting Thai noodle soups, curries, and fast-casual service on
              Burrard Street. Tap a logo to zoom in on the coverage.
            </p>
          </div>
          <div>
            <div className="flex items-center justify-between gap-3 mb-4">
              <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                <span className="inline-flex h-2 w-2 rounded-full bg-amber-500" aria-hidden />
                Tap logos to enlarge
              </p>
              <span className="hidden sm:inline-flex items-center gap-2 text-xs font-medium text-gray-700 bg-white border border-amber-100 rounded-full px-3 py-1 shadow-sm">
                View feature
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" aria-hidden>
                  <path d="M10 4l6 6-6 6m6-6H4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </div>
            <div className="rounded-3xl border border-amber-100 bg-amber-50/60 p-6 sm:p-8 shadow-md">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 items-stretch">
                {pressLogos.map((logo, idx) => (
                  <motion.button
                    key={logo.src}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.07, duration: 0.45 }}
                    onClick={() => setSelectedPress(logo)}
                    aria-label={`Open ${logo.label} feature`}
                    className="group relative flex flex-col items-center gap-3 bg-white rounded-2xl border border-amber-100 p-4 sm:p-5 shadow-sm hover:shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <div className="absolute right-3 top-3 text-[11px] uppercase tracking-wide text-amber-700 font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      Tap to zoom
                    </div>
                    <div className="relative w-full h-20 sm:h-24">
                      <Image
                        src={logo.src}
                        alt={logo.alt}
                        fill
                        sizes="(max-width: 640px) 320px, 240px"
                        className="object-contain"
                        priority={idx === 0}
                      />
                    </div>
                    <p className="text-sm font-semibold text-gray-800 text-center">{logo.label}</p>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {selectedPress && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedPress(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 22 }}
              className="bg-white rounded-3xl p-6 shadow-2xl max-w-3xl w-full relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedPress(null)}
                className="absolute right-3 top-3 text-gray-500 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-full p-1"
                aria-label="Close"
              >
                ×
              </button>
              <div className="relative w-full h-64 sm:h-80">
                <Image
                  src={selectedPress.src}
                  alt={selectedPress.alt}
                  fill
                  sizes="(max-width: 768px) 90vw, 800px"
                  className="object-contain"
                  priority
                />
              </div>
              <p className="mt-4 text-center text-base font-semibold text-gray-900">{selectedPress.label}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <section id="menu" className="section bg-white">
        <div className="container max-w-6xl">
          <div className="text-center mb-10">
            <p className="eyebrow">Menu highlights</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Thai classics, served fast-casual</h2>
            <p className="section-description mt-3">
              Appetizers, noodle soups, curries with rice, stir-fried noodles, rice plates, and desserts from our Burrard Street kitchen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredDishes.map((dish, i) => (
              <motion.div
                key={dish.name}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ delay: 0.05 * i, duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                className="card h-full border border-amber-100 bg-white"
              >
                <div className="flex justify-between items-start gap-3">
                  <h3 className="text-lg font-semibold text-gray-900">{dish.name}</h3>
                  <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">{dish.price}</span>
                </div>
                <p className="mt-3 text-sm text-gray-600 leading-relaxed">{dish.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {menuCategories.map((category) => (
              <span
                key={category}
                className="px-4 py-2 rounded-full bg-amber-50 text-sm font-semibold text-amber-900 border border-amber-100"
              >
                {category}
              </span>
            ))}
          </div>

          <div className="mt-8 flex justify-center">
            <Link href="/menu" className="btn btn-primary">
              View the full menu
            </Link>
          </div>
        </div>
      </section>

      <section className="section bg-amber-50">
        <div className="container max-w-6xl grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <p className="eyebrow text-amber-700">What guests love</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Fresh herbs, house-made pastes, comforting bowls</h2>
            <p className="mt-4 text-lg text-gray-700 leading-relaxed">
              From papaya salad and boat noodles to chuchi curry and pad kee mao, every dish uses homemade pastes, coconut milk,
              Thai basil, kaffir lime leaves, and krachai for bright, balanced flavor.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "House-made curry and stir-fry pastes",
                "Noodle soups with rich, aromatic broth",
                "Fast-casual service for dine-in or takeout",
                "Desserts like sticky rice rainbow and roti",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <span className="mt-1 block w-2 h-2 rounded-full bg-amber-500" />
                  <p className="text-sm text-gray-800">{item}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex gap-3">
              <Link href="/menu" className="btn btn-primary">
                Browse menu
              </Link>
              <Link href="tel:+16045596484" className="btn btn-secondary">
                Call to order
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {galleryImages.map((src, idx) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
                className={`rounded-3xl overflow-hidden shadow-lg border border-amber-100 ${
                  idx === 0 ? "col-span-2 h-64 md:h-80" : "h-40 md:h-48"
                }`}
              >
                <img src={src} alt="Unchai Thai dish" className="w-full h-full object-cover" loading="lazy" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="visit" className="section bg-white">
        <div className="container max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div className="space-y-4">
            <p className="eyebrow">Visit us</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Dine in on Burrard Street</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Join us for dine-in or call ahead for takeout. Unchai Thai Restaurant serves noodle soups, stir-fries,
              curries, rice dishes, and Thai desserts with homemade pastes and bright herbs.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="card bg-white border-amber-100">
                <h3 className="text-sm uppercase tracking-widest text-gray-500 mb-2">Address</h3>
                <p className="text-gray-800 font-semibold">
                  {liveVisitDetails.addressLine1}
                  <br />
                  {liveVisitDetails.addressLine2}
                </p>
                <Link
                  href={liveVisitDetails.mapLink}
                  className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-black hover:underline"
                >
                  Open in Google Maps
                </Link>
              </div>
              <div className="card bg-white border-amber-100">
                <h3 className="text-sm uppercase tracking-widest text-gray-500 mb-2">Hours</h3>
                <ul className="space-y-1 text-gray-700">
                  {liveVisitDetails.hours.map((entry) => (
                    <li key={entry.label} className="flex flex-col gap-1">
                      <span className="leading-snug">{entry.label}</span>
                      <span className="font-semibold leading-snug">{entry.value}</span>
                    </li>
                  ))}
                </ul>
                {liveVisitDetails.kitchenCloseDisplay && (
                  <p className="mt-3 text-sm text-gray-600">
                    Kitchen closes at {liveVisitDetails.kitchenCloseDisplay}. Closed on Mondays.
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link href={liveVisitDetails.phoneHref} className="btn btn-primary">
                Call {liveVisitDetails.phone}
              </Link>
              <Link href={`mailto:${liveVisitDetails.email}`} className="btn btn-secondary">
                Email us
              </Link>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl overflow-hidden shadow-xl border border-amber-100"
          >
            <div className="aspect-[4/3] w-full bg-gray-100 relative">
              <iframe
                title="Unchai Thai Restaurant map"
                className="absolute inset-0 w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2602.622188034942!2d-123.14853912327282!3d49.26501157139656!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x548673a7b6e490f3%3A0x69d07fa2ed2ef35d!2sUnchai%20Restaurant!5e0!3m2!1sen!2sca!4v1717459200000!5m2!1sen!2sca"
                allowFullScreen
              />
            </div>
          </motion.div>
        </div>
      </section>
      {debugInfo && (
        <div className="fixed bottom-4 right-4 z-50 w-72 rounded-2xl border border-amber-200 bg-white/95 p-4 text-xs shadow-lg backdrop-blur-sm">
          <div className="font-semibold text-amber-900">API Debug</div>
          <div className="mt-2 space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Server</div>
            {renderDebugRow('menu', debugInfo.server.menu)}
            {renderDebugRow('hours', debugInfo.server.hours)}
            {renderDebugRow('contact', debugInfo.server.contact)}
          </div>
          <div className="mt-3 space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Client</div>
            {renderDebugRow('menu', clientDebug.menu)}
            {renderDebugRow('hours', clientDebug.hours)}
            {renderDebugRow('contact', clientDebug.contact)}
            {renderDebugRow('notices', clientDebug.notices)}
          </div>
          <div className="mt-3 text-[10px] text-gray-500">
            Base: {resolveApiUrl('/api/menu').replace('/api/menu', '') || 'relative'}
          </div>
        </div>
      )}
    </div>
  );
}
